import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Shield, Users, LayoutDashboard, DollarSign, BarChart3, ClipboardList,
  Activity, LogOut, Eye, Settings as SettingsIcon, Terminal, Bell,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isImpersonating, getImpersonationMeta, stopImpersonation } from "@/lib/api";

const NAV = [
  { to: "/admin",                 label: "Overview",         icon: LayoutDashboard, end: true },
  { to: "/admin/users",           label: "Users",            icon: Users },
  { to: "/admin/accounts",        label: "Accounts",         icon: Activity },
  { to: "/admin/analytics",       label: "Analytics",        icon: BarChart3 },
  { to: "/admin/llm-usage",       label: "LLM Usage",        icon: DollarSign },
  { to: "/admin/impersonation-log", label: "Impersonation Log", icon: ClipboardList },
  // Admin-only operator tools — routes render under AppLayout but are gated by RequireAdmin.
  { to: "/connect",               label: "Connect MT5",      icon: Terminal },
  { to: "/alerts",                label: "Alerts",           icon: Bell },
];

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [impersonating, setImpersonating] = useState(isImpersonating());
  const [meta, setMeta] = useState(getImpersonationMeta());

  useEffect(() => {
    // Watch localStorage for cross-tab impersonation changes
    const onStorage = () => {
      setImpersonating(isImpersonating());
      setMeta(getImpersonationMeta());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const exitImpersonation = () => {
    stopImpersonation();
    toast.success("Exited impersonation");
    setImpersonating(false);
    setMeta(null);
    // Reload so all cached data is fetched with admin token again
    window.location.href = "/admin/users";
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0a0e1a] text-[#e5e7eb]" data-testid="admin-layout">
      {/* Impersonation banner — YELLOW — highest priority */}
      {impersonating && (
        <div
          className="w-full px-4 py-2 flex items-center justify-between text-xs font-mono"
          style={{ background: "linear-gradient(90deg, #fbbf2422, #fbbf2411)", borderBottom: "1px solid #fbbf24" }}
          data-testid="impersonation-banner"
        >
          <div className="flex items-center gap-2 text-[#fbbf24]">
            <Eye className="w-3.5 h-3.5" />
            <span className="font-bold">IMPERSONATING</span>
            <span className="text-[#e5e7eb]">{meta?.target?.email || "(user)"}</span>
            <span className="text-[#9ca3af]">· short-lived session (30 min)</span>
          </div>
          <button
            onClick={exitImpersonation}
            className="px-2 py-0.5 rounded border border-[#fbbf24]/40 hover:bg-[#fbbf24]/10 text-[#fbbf24] font-bold"
            data-testid="exit-impersonation-btn"
          >
            EXIT ×
          </button>
        </div>
      )}

      {/* Admin-mode banner — RED */}
      <div
        className="w-full px-4 py-2 flex items-center justify-between text-xs font-mono"
        style={{ background: "linear-gradient(90deg, #dc262622, #dc262611)", borderBottom: "1px solid #dc2626" }}
        data-testid="admin-mode-banner"
      >
        <div className="flex items-center gap-2 text-[#dc2626]">
          <Shield className="w-3.5 h-3.5" />
          <span className="font-bold tracking-widest">ADMIN MODE</span>
          <span className="text-[#9ca3af]">· {user?.email}</span>
        </div>
        <div className="flex items-center gap-3 text-[#9ca3af]">
          <button
            onClick={() => navigate("/dashboard")}
            className="hover:text-[#e5e7eb]"
            data-testid="switch-to-user-panel-btn"
            title="Switch to user panel (keeps admin token)"
          >
            Switch to User Panel →
          </button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        <aside
          className="w-60 shrink-0 border-r border-[#1f2937] bg-[#0a0e1a] flex flex-col"
          data-testid="admin-sidebar"
        >
          <div className="h-14 flex items-center px-5 border-b border-[#1f2937]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-[#dc2626]/10 border border-[#dc2626]/40 flex items-center justify-center">
                <Shield className="w-4 h-4 text-[#dc2626]" strokeWidth={2} />
              </div>
              <div>
                <div className="font-heading font-black tracking-tighter text-lg leading-none text-[#dc2626]">
                  ADMIN
                </div>
                <div className="text-[9px] font-mono tracking-widest text-[#6b7280] uppercase mt-1">
                  Veerox Console
                </div>
              </div>
            </div>
          </div>

          <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`admin-nav-${to === "/admin" ? "overview" : to.split("/").pop()}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded text-xs font-mono uppercase tracking-widest transition-colors ${
                    isActive
                      ? "bg-[#dc2626]/15 text-[#dc2626] border border-[#dc2626]/40"
                      : "text-[#9ca3af] hover:bg-[#1f2937]/50 hover:text-[#e5e7eb] border border-transparent"
                  }`
                }
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="p-3 border-t border-[#1f2937] space-y-2">
            <NavLink
              to="/settings"
              className="flex items-center gap-2 px-3 py-2 rounded text-xs font-mono text-[#9ca3af] hover:bg-[#1f2937]/50 hover:text-[#e5e7eb]"
              data-testid="admin-nav-settings"
            >
              <SettingsIcon className="w-3.5 h-3.5" /> Settings
            </NavLink>
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded text-xs font-mono text-[#ef4444] hover:bg-[#ef4444]/10"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </aside>

        <main className="flex-1 min-h-0 overflow-auto p-6 bg-[#0a0e1a]">
          <Outlet />
        </main>
      </div>

      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "#111827", color: "#e5e7eb", border: "1px solid #dc2626" },
      }} />
    </div>
  );
};

export default AdminLayout;
